import React, { useState, useMemo, useEffect } from "react";
import { Modal, Row, Col, Form } from "react-bootstrap";
import { ColDef } from "ag-grid-community";
import { ThreeCircles } from "react-loader-spinner";

import { fileToBase64, optionRenderer } from "./RulesModal.utils";
import { validateRuleForm, buildNewRow, fetchTemplateFieldRulesDetails } from "./RulesModal.service";
import {
  RulesModalProps,
  TemplateRuleRow,
  OptionType,
  SubstitutionType,
  SubstitutionTypeOption,
} from "./RulesModal.types";

import ActionBtnsCellRenderer from "./components/ActionsRenderer/ActionsRenderer";
import CustomButton from "../../../../../pages/reusable_components/custom_button_component/Button";
import CygnusGrid from "../../../../../pages/reusable_components/CygnusGrid/CygnusGrid";
import alertModalStyles from "../../../../../Reusables/components/AlertModal/AlertModal.module.css";
import wetSignStyles from "../../../WetSignUploadModal/WetSignUploadModal.module.css";
import styles from "./RulesModal.module.css";

import TextInput from "../../../../../Reusables/components/TextInput/TextInput";
import SelectInput from "../../../../../Reusables/components/SelectInput/SelectInput";
import Textarea from "../../../../../Reusables/components/TextareaInput/TextareaInput";
import { FormContext } from "../../../../reusable_components/input_fields/FormContext";
import Element from "../../../../reusable_components/input_fields/Input";

import {
  saveTemplateFieldConfigDetails,
} from "../../RulesPage.service";


const RulesModal: React.FC<RulesModalProps> = (props) => {
  const [localRuleData, setLocalRuleData] = useState(props.ruleData);
  const [ruleName, setRuleName] = useState(localRuleData?.RuleName ?? "");
  const [selectedField, setSelectedField] = useState<OptionType | null>(null);
  const [operator, setOperator] = useState<OptionType>({
    id: "=",
    value: "=",
    label: "=",
  });
  const [conditionValue, setConditionValue] = useState("");
  const [defaultValue, setDefaultValue] = useState<OptionType>({
    id: "Blank",
    value: "Blank",
    label: "Blank",
  });
  const [substitutionType, setSubstitutionType] =
    useState<SubstitutionType>("TEXT");
  const [substitutionValue, setSubstitutionValue] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [rows, setRows] = useState<TemplateRuleRow[]>([]);
  const [editingRow, setEditingRow] = useState<TemplateRuleRow | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [apiInProgress, setApiInProgress] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  
  const operatorsObject = { 0: "=", 1: "!=" };
  const defaultValuesObject = { 0: "Blank", 1: "Data Source" };
  const gridStyle = useMemo(() => ({ height: "25vh", width: "100%" }), []);
  const [mode, setMode] = useState(props.mode);

  const isViewMode = mode === "view";
  const isEditMode = mode === "edit";

  const [imageElements, setImageElements] = useState([
    {
      id: "substitutionImage",
      type: "file",
      label: "Substitution Image",
      value: imageFile,
      required: true,
      accept: ".png,.jpg,.jpeg",
      example: null,
      mandatory: true,
      approval: false,
      disabled: isViewMode,
      pivot: true,
      pivotreverse: false,
      width: 12,
      isValid: !errors.substitutionImage,
      error: errors.substitutionImage,
    },
  ]);

  useEffect(() => {
    setLocalRuleData(props.ruleData);
  }, [props.ruleData]);
  
  useEffect(() => {
    setMode(props.mode);
  }, [props.mode]);

  useEffect(() => {
    setImageElements(prev => [
      {
        ...prev[0],
        value: imageFile,
        error: errors.substitutionImage,
      },
    ]);
  }, [imageFile, errors.substitutionImage]);

  useEffect(() => {
    if (substitutionType === "TEXT") {
      setImageFile(null);
      setImagePreview(null);
    }
  }, [substitutionType]);

  useEffect(() => {
    const loadRuleDetails = async () => {
      if (!["edit", "view"].includes(mode) || !localRuleData?.TemplateRuleId) return;

      setShowLoader(true);
      try {
        const data = await fetchTemplateFieldRulesDetails({
          TemplateRuleId: localRuleData.TemplateRuleId,
        });
        setRows(data);
      } finally {
        setShowLoader(false);
      }
    };

    loadRuleDetails();
  }, [mode, localRuleData?.TemplateRuleId]);

  useEffect(() => {
    if (localRuleData?.SelectedFieldLabel) {
      setSelectedField({
        id: localRuleData?.SelectedFieldValue,
        value: localRuleData?.SelectedFieldValue,
        label: localRuleData?.SelectedFieldLabel,
      });
    }
  }, [localRuleData?.SelectedFieldLabel]);

  const refreshRuleDetails = async (ruleId?: number) => {
    const id = ruleId ?? localRuleData?.TemplateRuleId;
    if (!id) return;

    setShowLoader(true);
    try {
      const data = await fetchTemplateFieldRulesDetails({
        TemplateRuleId: id,
      });
      setRows(data);
    } finally {
      setShowLoader(false);
    }
  };

  const resetForm = () => {
    setOperator({ id: "=", value: "=", label: "=" });
    setConditionValue("");
    setSubstitutionValue("");
    setSubstitutionType("TEXT");
    setImageFile(null);
    setImagePreview(null);
    setEditingRow(null);
    setErrors({});
  };

  const saveRuleCondition = async () => {
    const { valid, errors } = validateRuleForm(
      ruleName,
      selectedField,
      operator,
      conditionValue,
      defaultValue,
      substitutionValue,
      rows,
      editingRow,
      props.selectedFieldsList,
      localRuleData?.RuleName,
      substitutionType,
      imageFile
    );

    if (!valid) {
      setErrors(errors);
      if (substitutionType === "IMAGE") {
        setImageElements(prev => [
          {
            ...prev[0],
            error: errors.substitutionImage,
        touched: true,
        selected: true,
          },
        ]);
      }
      return;
    }

    setApiInProgress(true);

    try {
      let finalValue = substitutionValue;
      let substitutionImage: string | undefined;

      if (substitutionType === "IMAGE" && imageFile) {
        finalValue = "";
        substitutionImage = await fileToBase64(imageFile);
      }

      const payload = buildNewRow(
        ruleName,
        selectedField,
        operator,
        conditionValue,
        defaultValue,
        finalValue,
        substitutionType,
        substitutionImage,
        editingRow,
        localRuleData?.TemplateRuleId
      );

      const response = await saveTemplateFieldConfigDetails([payload]);

      if(response?.success) {
        const newRuleId = response?.data?.[0]?.TemplateRuleId;

        setLocalRuleData?.({
          ...localRuleData,
          TemplateRuleId: newRuleId,
        });
        setMode("edit");
        await refreshRuleDetails(newRuleId);
        resetForm();
      }
    } finally {
      setApiInProgress(false);
    }
  };

  const deleteRuleCondition = async (row: TemplateRuleRow) => {
    const payload = { ...row, Active: "N" as const };
    await saveTemplateFieldConfigDetails([payload], true);

    setRows((prev) =>
      prev.filter((r) => r.TemplateRuleDetailId !== row.TemplateRuleDetailId)
    );
  };

  const columnDefs: ColDef<TemplateRuleRow>[] = [
    { headerName: "Selected Field", field: "SelectedFieldLabel", flex: 1.5, filter: false },
    { headerName: "Operator", field: "Operator", flex: 1, filter: false },
    { headerName: "Condition Value", field: "ConditionValue", flex: 1, filter: false },
    {
      headerName: "Type",
      field: "SubstitutionType",
      flex: 1,
      valueFormatter: (p) => (p.value === "IMAGE" ? "Image" : "Text"), 
      filter: false
    },
    { headerName: "Substitution Value", field: "SubstitutionValue", flex: 2, filter: false },
    {
      headerName: "Action",
      pinned: "right",
      maxWidth: 80,
      cellRenderer: (params: any) => (
        <ActionBtnsCellRenderer
          mode={mode}
          data={params.data}
          onEdit={async(row) => {
            setEditingRow(row);
            setSelectedField({
              id: row.SelectedFieldValue,
              value: row.SelectedFieldValue,
              label: row.SelectedFieldLabel,
            });
            setOperator({
              id: row.Operator,
              value: row.Operator,
              label: row.Operator,
            });
            setConditionValue(row.ConditionValue);
            setDefaultValue({
              id: row.DefaultValue,
              value: row.DefaultValue,
              label: row.DefaultValue,
            });
            setSubstitutionType(row.SubstitutionType ?? "TEXT");
            setSubstitutionValue(row.SubstitutionValue);
            setImagePreview(row.SubstitutionImage ?? null);

            if (row.SubstitutionType?.toLowerCase() === "image" && row.TemplateRuleDetailId) {
              setShowLoader(true);
              try {
                const data = await fetchTemplateFieldRulesDetails({
                  TemplateRuleDetailId: row.TemplateRuleDetailId,
                  SubstitutionType: row.SubstitutionType
                });

                const ruleDetail = data?.[0];
                const rawImage = ruleDetail?.SubstitutionImage;

                if (rawImage) {
                  const imageSrc = rawImage.startsWith("data:image/")
                    ? rawImage
                    : `data:image/png;base64,${rawImage}`;

                  setImagePreview(imageSrc);
                } else {
                  setImagePreview(null);
                }
              } finally {
                setShowLoader(false);
              }
            } else {
              setImagePreview(null);
            }
          }}
          onDelete={() => deleteRuleCondition(params.data)}
        />
      ),
      filter: false
    },
  ];

  const substitutionTypeOptions: SubstitutionTypeOption[] = [
    { id: "TEXT", value: "TEXT", label: "Text" },
    { id: "IMAGE", value: "IMAGE", label: "Image" },
  ];

  return (
    <Modal
      {...props}
      show
      size="lg"
      centered
      backdrop="static"
      keyboard={false}
      dialogClassName={alertModalStyles.dialog}
      contentClassName={alertModalStyles.content}
      backdropClassName={alertModalStyles.backdrop}
    >
      {showLoader && (
        <div className="disable-editor-overlay blurr-background">
          <div className="flex-row-center-center">
            <ThreeCircles
              height="35"
              width="35"
              color="#4fa94d"
              wrapperStyle={{}}
              wrapperClass=""
              visible={true}
              ariaLabel="three-circles-rotating"
              outerCircleColor="var(--theme1_tertiary)"
              innerCircleColor="var(--theme1_secondary)"
              middleCircleColor="var(--theme1_primary)"
            />
            <h6 style={{ marginLeft: 10 }}>Loading your data</h6>
          </div>
        </div>
      )}

      <Modal.Header closeButton className={alertModalStyles.header}>
        <Modal.Title className={alertModalStyles.message}>
          Configurable Rules
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        
        <Row>
          <Col md={4}>
            <TextInput
              field={{
                id: "ruleName",
                type: "text",
                label: "Rule Name",
                textStyle: { fontSize: 11 },
                value: ruleName,
                isValid: !errors.ruleName,
                error: errors.ruleName,
                show: true,
                validation: [{ type: "required", message: "Required!" }],
                disabled: isViewMode
              }}
              handleChange={(id, e) => setRuleName(e.target.value)}
              handleBlur={() => {}}
              handleSelect={() => {}}
            />
          </Col>

          <Col md={4}>
            <SelectInput
              field={{
                id: "selectedField",
                type: "select",
                label: "Selected Field",
                textStyle: { fontSize: 11 },
                value: selectedField?.value,
                options: props.selectedFieldsList,
                isValid: !errors.selectedField,
                error: errors.selectedField,
                show: true,
                validation: [{ type: "required", message: "Required!" }],
                disabled: (isViewMode || isEditMode)
              }}
              handleChange={(id, val) => setSelectedField(val as OptionType)}
              handleBlur={() => {}}
              handleSelect={() => {}}
            />
          </Col>

          <Col md={4}>
            <SelectInput
              field={{
                id: "defaultValue",
                type: "select",
                label: "Default Value",
                textStyle: { fontSize: 11 },
                value: defaultValue?.value,
                options: optionRenderer(defaultValuesObject),
                show: true,
                validation: [{ type: "required", message: "Required!" }],
                disabled: isViewMode
              }}
              handleChange={(id, val) => setDefaultValue(val as OptionType)}
              handleBlur={() => {}}
              handleSelect={() => {}}
            />
          </Col>
        </Row>

        <Row className="mt-3">
          <Col md={4}>
            <SelectInput
              field={{
                id: "operator",
                type: "select",
                label: "Operator",
                textStyle: { fontSize: 11 },
                value: operator.value,
                options: optionRenderer(operatorsObject),
                show: true,
                validation: [{ type: "required", message: "Required!" }],
                disabled: isViewMode
              }}
              handleChange={(id, val) => setOperator(val as OptionType)}
              handleBlur={() => {}}
              handleSelect={() => {}}
            />
          </Col>

          <Col md={4}>
            <TextInput
              field={{
                id: "conditionValue",
                type: "text",
                label: "Condition Value",
                textStyle: { fontSize: 11 },
                value: conditionValue,
                isValid: !errors.conditionValue,
                error: errors.conditionValue,
                show: true,
                validation: [{ type: "required", message: "Required!" }],
                disabled: isViewMode
              }}
              handleChange={(id, e) => setConditionValue(e.target.value)}
              handleBlur={() => {}}
              handleSelect={() => {}}
            />
          </Col>

          <Col md={4}>
            <SelectInput
              field={{
                id: "substitutionType",
                type: "select",
                label: "Substitution Type",
                textStyle: { fontSize: 11 },
                value: substitutionType,
                options: substitutionTypeOptions,
                show: true,
                validation: [{ type: "required", message: "Required!" }],
                disabled: isViewMode
              }}
              handleChange={(id, val) => setSubstitutionType(val?.value as SubstitutionType)}
              handleBlur={() => {}}
              handleSelect={() => {}}
            />
          </Col>
        </Row>

        <Row className="mt-3">
          <Col md={10}>
            {substitutionType === "TEXT" ? (
              <Textarea
                field={{
                  id: "substitutionValue",
                  type: "textarea",
                  label: "Substitution Value",
                  textStyle: { fontSize: 11 },
                  value: substitutionValue,
                  isValid: !errors.substitutionValue,
                  error: errors.substitutionValue,
                  show: true,
                  validation: [{ type: "required", message: "Required!" }],
                  disabled: isViewMode
                }}
                handleChange={(id, e) => setSubstitutionValue(e.target.value)}
                handleBlur={() => {}}
                handleSelect={() => {}}
              />
            ) : (
            <>
              <FormContext.Provider
                value={{
                  handleChange: (fieldId, value) => {
                    const file =
                      Array.isArray(value) && value.length > 0 ? value[0] : null;

                    setImageFile(file);

                    setImageElements(prev => [
                      { ...prev[0], value: value },
                    ]);
                  }
                }}
              >
                <Form>
                  <Row className={styles.fileUploadLabel}>
                    {imageElements.map((field, i) => (
                      <Element key={i} field={field} md={12} />
                    ))}

                    {imageElements[0].value && (
                      <div className={wetSignStyles.filePreviewContainer}>
                        <div className={wetSignStyles.filePreview}>
                          <div className={wetSignStyles.fileLeft}>
                            <span className={wetSignStyles.fileIcon}>🖼️</span>
                            <span className={wetSignStyles.fileName}>
                              {imageElements[0].value.name}
                            </span>
                          </div>

                          <span
                            className={wetSignStyles.removeIcon}
                            onClick={() => {
                              setImageFile(null);
                              setImageElements(prev => [
                                { ...prev[0], value: null },
                              ]);
                            }}
                          >
                            ✖
                          </span>
                        </div>
                      </div>
                    )}
                  </Row>
                </Form>
              </FormContext.Provider>
            </>
            )}
          </Col>

          <Col md={2} className="d-flex align-items-end">
            <CustomButton
              content={editingRow ? "Update" : "Add"}
              progress={apiInProgress}
              onclick={saveRuleCondition}
            />
          </Col>
        </Row>

        <Row className="mt-3">
          <Col md={12}>
            <div className="ag-theme-balham" style={gridStyle}>
              <CygnusGrid
                fetchData={async () => ({ records: rows, totalCount: rows.length })}
                rowModelType="clientSide"
                dataIdentifier="TemplateRuleDetailId"
                cachedData={rows}
                setCachedData={setRows}
                columnDefs={columnDefs}
                rowSelection={undefined}
              />
            </div>
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer>
        <CustomButton content="Cancel" type="quinary" onclick={() => props.onHide()} />
        <CustomButton content="Okay" type="tertiary" onclick={() => props.onHide(true)} />
      </Modal.Footer>
    </Modal>
  );
};

export default RulesModal;
